import { Component, Input, OnDestroy, OnInit, Inject } from "@angular/core";
import { NgIf, NgFor } from "@angular/common";
import { BloodReportHistoryComponent } from "../../member-dashboard/blood-report-history/blood-report-history.component";
import {
  HttpClient,
  HttpClientModule,
  HttpHeaders,
} from "@angular/common/http";
import {
  MatDialog,
  MatDialogModule,
  MAT_DIALOG_DATA,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
import { environment } from "../../../environments/environment";
import { GroceryEditorDialogComponent } from "./grocery-editor-dialog.component";
import { ToastService } from "../../services/toast.service";
import { SharedServiceService } from "../../services/shared-service.service";
import { MemberService } from "../member-service/MemberService ";



@Component({
  selector: "app-md-health-tools",
  standalone: true,
  imports: [
    NgIf,
    MatDialogModule,
    HttpClientModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    BloodReportHistoryComponent,
  ],
  templateUrl: "./md-health-tools.component.html",
  styleUrl: "./md-health-tools.component.css",
})
export class MdhHealthToolsComponent implements OnInit, OnDestroy {
  @Input() memberDetail: any;
  @Input() memberId: string = "";

  showBloodReportHistory: boolean = false;

  bloodReportToolBaseUrl: string = environment.bloodReportToolUrl;
  glucoseDataToolBaseUrl: string = environment.glucoseDataToolUrl;
  biweeklyReportToolBaseUrl: string = environment.biweeklyReportToolUrl;
  nightlyNutritionScoreBaseUrl: string = environment.NightlyNutritionScore;
  currentDayNutritionScoreBaseUrl: string = environment.CurrentDayNutritionScore;
  private groceryApiUrl: string = environment.groceryListApiUrl;
  groceryProcessing: boolean = false;
  groceryCompleted: boolean = false;
  groceryButtonText: string = "Download PDF";
  private dialogClosed: boolean = false;
  favoriteRestaurantsLoading: boolean = false;
  latestBiweeklyReport: { driveFileId: string } | null = null;
  // Simple form to capture grocery insights before generating the list
  groceryInsightsForm: FormGroup = this.formBuilder.group({
    fromDate: [null],
    toDate: [null],
    preferredStore: [""],
    // keep mealDate for backward compatibility / future use
    mealDate: [null],
    foodPreferences: [""],
    allergies: [""],
    notes: [""],
  });

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private toastService: ToastService,
    private sharedService: SharedServiceService,
    private formBuilder: FormBuilder,
    private memberService: MemberService
  ) {
    console.log("Grocery API URL:", this.groceryApiUrl);
  }

  ngOnInit() {
    if (this.memberId) {
      this.loadLatestBiweeklyReport();
    }
  }

  private loadLatestBiweeklyReport(): void {
    this.memberService.getBiweeklyReportsLatest(this.memberId).subscribe({
      next: (data) => {
        if (data?.result?.biWeeklyReports?.length) {
          this.latestBiweeklyReport = data.result.biWeeklyReports[0];
        } else {
          this.latestBiweeklyReport = null;
        }
      },
      error: () => {
        this.latestBiweeklyReport = null;
      },
    });
  }

  ngOnDestroy() {}

  private getFirstName(): string | undefined {
    return this.memberDetail?.firstName?.trim();
  }

  private getLastName(): string | undefined {
    return this.memberDetail?.lastName?.trim();
  }

  // loadAvailableStores kept for future use but not currently called

  generateBloodReportToolLink(): string {
    const firstName = this.getFirstName();
    const lastName = this.getLastName();
    if (!firstName || !lastName || !this.memberId) {
      return this.bloodReportToolBaseUrl;
    }
    const params = new URLSearchParams({
      first_name: firstName,
      last_name: lastName,
      member_id: this.memberId.toString(),
    });
    return `${this.bloodReportToolBaseUrl}/?${params.toString()}`;
  }

  openBloodReportTool(): void {
    const url = this.generateBloodReportToolLink();
    window.open(url, "_blank");
  }

  generateGlucoseDataToolLink(): string {
    const firstName = this.getFirstName();
    const lastName = this.getLastName();
    if (!firstName || !lastName || !this.memberId) {
      return this.glucoseDataToolBaseUrl;
    }
    const params = new URLSearchParams({
      first_name: firstName,
      last_name: lastName,
      member_id: this.memberId.toString(),
    });
    return `${this.glucoseDataToolBaseUrl}?${params.toString()}`;
  }

  openGlucoseDataTool(): void {
    const url = this.generateGlucoseDataToolLink();
    window.open(url, "_blank");
  }

  generateBiweeklyReportToolLink(): string {
    if (!this.memberId) {
      return this.biweeklyReportToolBaseUrl;
    }
    return `${this.biweeklyReportToolBaseUrl}${this.memberId}/reports`;
  }

  openBiweeklyReportTool(): void {
    const url = this.generateBiweeklyReportToolLink();
    window.open(url, "_blank");
  }

  private getNutritionScoreEnv(): string {
    return environment.production ? "prod" : "stage";
  }

  private getTodayDateString(): string {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  generateNightlyNutritionScoreUrl(): string {
    const envParam = this.getNutritionScoreEnv();
    const params = new URLSearchParams({
      member_id: this.memberId || "",
      source_env: envParam,
      target_env: envParam,
      date: this.getTodayDateString(),
    });
    return `${this.nightlyNutritionScoreBaseUrl}?${params.toString()}`;
  }

  generateCurrentDayNutritionScoreUrl(): string {
    const envParam = this.getNutritionScoreEnv();
    const params = new URLSearchParams({
      member_id: this.memberId || "",
      source_env: envParam,
      target_env: envParam,
      date: this.getTodayDateString(),
    });
    return `${this.currentDayNutritionScoreBaseUrl}?${params.toString()}`;
  }

  openLastGeneratedReport(): void {
    if (!this.latestBiweeklyReport?.driveFileId) {
      return;
    }
    const url = `https://drive.google.com/file/d/${this.latestBiweeklyReport.driveFileId}/view?usp=sharing`;
    window.open(url, "_blank");
  }

  openGroceryListTool() {
    if (!this.memberId) {
      this.toastService.errorMessage(
        "Member ID not found. Please refresh the page."
      );
      return;
    }

    // Open a larger dialog to capture insights before starting processing.
    const insightsDialog = this.dialog.open(GroceryInsightsDialogComponent, {
      width: "640px",
      maxWidth: "95vw",
      disableClose: false,
      panelClass: "grocery-insights-dialog-panel",
      data: {
        memberId: this.memberId,
        form: this.groceryInsightsForm,
      },
    });

    insightsDialog.afterClosed().subscribe((result) => {
      if (result !== "start") {
        return;
      }

      this.dialogClosed = false;

      const dialogRef = this.dialog.open(GroceryStatusDialogComponent, {
        width: "500px",
        maxWidth: "95vw",
        disableClose: true,
        data: {
          status: "starting",
          message: "Initiating grocery list processing...",
          memberId: this.memberId,
        },
      });

      dialogRef.afterClosed().subscribe(() => {
        this.dialogClosed = true;
      });

      this.groceryProcessing = true;
      this.groceryButtonText = "Processing...";

      // Call grocery API directly - CORS is configured
      const apiUrl = `${this.groceryApiUrl}/get_recommendations`;
      console.log("Calling grocery API:", apiUrl);
      console.log("Member ID:", this.memberId);

      // Prepare request body with member data and insights if available
      const requestBody: any = {
        member_id: this.memberId,
      };

      // If member detail is available, include address information
      if (this.memberDetail) {
        const address = this.memberDetail.address || {};
        // Check if address exists and has required fields
        if (
          address.addressLine1 ||
          address.city ||
          this.memberDetail.addressLine1
        ) {
          requestBody.member_address = address.addressLine1
            ? address
            : {
                addressLine1: this.memberDetail.addressLine1 || "",
                addressLine2: this.memberDetail.addressLine2 || "",
                city: this.memberDetail.city || "",
                state: this.memberDetail.state || "",
                zipCode: this.memberDetail.zipCode || "",
                country: this.memberDetail.country || "",
              };
          console.log(
            "Including member address in request:",
            requestBody.member_address
          );
        }
      }

      // Add optional insights from the grocery form
      const insights = this.groceryInsightsForm?.value;
      if (insights) {
        if (insights.fromDate) {
          requestBody.from_date = insights.fromDate;
        }
        if (insights.toDate) {
          requestBody.to_date = insights.toDate;
        }
        if (insights.preferredStore?.trim()) {
          requestBody.preferred_store = insights.preferredStore.trim();
        }
        if (insights.mealDate) {
          requestBody.meal_date = insights.mealDate;
        }
        if (insights.foodPreferences?.trim()) {
          requestBody.food_preferences = insights.foodPreferences.trim();
        }
        if (insights.allergies?.trim()) {
          requestBody.allergies = insights.allergies.trim();
        }
        if (insights.notes?.trim()) {
          requestBody.notes = insights.notes.trim();
        }
      }

      this.http.post(apiUrl, requestBody).subscribe({
        next: () => {
          this.groceryProcessing = false;
          this.groceryCompleted = true;
          this.groceryButtonText = "Download PDF";
          if (!this.dialogClosed && dialogRef?.componentInstance) {
            dialogRef.close();
          }
        this.toastService.successMessage(
          "Grocery list generated. Click 'Download PDF' to get the latest version."
        );
        },
        error: (error) => {
          this.groceryProcessing = false;
          this.groceryButtonText = "Open existing";
          const errorMsg =
            error.error?.detail || error.message || "Failed to start processing.";
          if (!this.dialogClosed && dialogRef?.componentInstance) {
            dialogRef.componentInstance.updateStatus(
              "error",
              `❌ Error: ${errorMsg}`
            );
          } else {
            this.toastService.errorMessage(
              `Failed to generate grocery list: ${errorMsg}`
            );
          }
        },
      });
    });
  }

  openExistingGroceryList() {
    if (!this.memberId) {
      this.toastService.errorMessage("Member ID not found");
      return;
    }

    this.groceryProcessing = true;

    // Only check if an editable grocery list exists.
    // Do NOT download the PDF here – that is handled by downloadGroceryPDF().
    this.http
      .get(`${this.groceryApiUrl}/get_editable_results/${this.memberId}`)
      .subscribe({
        next: () => {
          this.groceryProcessing = false;
          this.groceryCompleted = true;
          this.groceryButtonText = "Download PDF";
          this.toastService.successMessage(
            "Existing grocery list found. Click 'Download PDF' to download the latest version."
          );
        },
        error: (err) => {
          this.groceryProcessing = false;
          this.groceryCompleted = false;
          if (err.status === 404) {
            this.toastService.errorMessage(
              "No existing grocery list found. Please create a new one first."
            );
          } else {
            this.toastService.errorMessage(
              "Failed to check for an existing grocery list."
            );
          }
        },
      });
  }
  openGroceryEditor() {
    if (!this.memberId) {
      this.toastService.errorMessage("Member ID not found.");
      return;
    }

    this.dialog.open(GroceryEditorDialogComponent, {
      width: "95vw",
      maxWidth: "1400px",
      height: "90vh",
      data: { memberId: this.memberId },
    });
  }

  openFavoriteRestaurants(): void {
    if (!this.memberId) {
      this.toastService.errorMessage(
        "Member ID not found. Please refresh the page."
      );
      return;
    }

    this.favoriteRestaurantsLoading = true;
    const url = `${environment.favoriteRestaurantsApiUrl}getFavouriteRestaurants`;
    const headers = this.sharedService.createAuthorizationHeaderForRestaurant();

    this.http
      .post<{ success: boolean; count: number; favorites: any[] }>(
        url,
        { memberId: this.memberId },
        { headers }
      )
      .subscribe({
        next: (data) => {
          this.favoriteRestaurantsLoading = false;
          if (data?.success && Array.isArray(data.favorites)) {
            this.dialog.open(FavoriteRestaurantsDialogComponent, {
              width: "560px",
              maxWidth: "95vw",
              panelClass: "favorite-restaurants-dialog",
              data: { favorites: data.favorites, memberId: this.memberId },
            });
          } else {
            this.toastService.errorMessage(
              "Failed to load favorite restaurants."
            );
          }
        },
        error: (err) => {
          this.favoriteRestaurantsLoading = false;
          const msg =
            err.error?.error_message ||
            err.error?.error ||
            err.message ||
            "Failed to load favorite restaurants.";
          this.toastService.errorMessage(msg);
        },
      });
  }

  openAddRestaurantDialog(): void {
    if (!this.memberId) {
      this.toastService.errorMessage(
        "Member ID not found. Please refresh the page."
      );
      return;
    }

    this.dialog.open(AddRestaurantDialogComponent, {
      width: "500px",
      maxWidth: "95vw",
      panelClass: "add-restaurant-dialog",
      data: { memberId: this.memberId },
    });
  }

  downloadGroceryPDF() {
    if (!this.memberId) {
      this.toastService.errorMessage("Member ID not found");
      return;
    }

    this.groceryButtonText = "Downloading...";

    const requestBody: any = { member_id: this.memberId };

    // Add optional insights from the grocery form so the PDF uses the same context
    const insights = this.groceryInsightsForm?.value;
    if (insights) {
      if (insights.fromDate) {
        requestBody.from_date = insights.fromDate;
      }
      if (insights.toDate) {
        requestBody.to_date = insights.toDate;
      }
      if (insights.preferredStore?.trim()) {
        requestBody.preferred_store = insights.preferredStore.trim();
      }
      if (insights.mealDate) {
        requestBody.meal_date = insights.mealDate;
      }
      if (insights.foodPreferences?.trim()) {
        requestBody.food_preferences = insights.foodPreferences.trim();
      }
      if (insights.allergies?.trim()) {
        requestBody.allergies = insights.allergies.trim();
      }
      if (insights.notes?.trim()) {
        requestBody.notes = insights.notes.trim();
      }
    }

    // Call grocery API directly - CORS is configured
    this.http
      .post(`${this.groceryApiUrl}/download_pdf`, requestBody, {
        responseType: "blob",
      })
      .subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `grocery_list_${this.memberId}_${Date.now()}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          this.groceryButtonText = "Download PDF";
          this.toastService.successMessage("PDF downloaded successfully!");
        },
        error: () => {
          this.groceryButtonText = "Download PDF";
          this.toastService.errorMessage("Error downloading PDF.");
        },
      });
  }
}

/* ---------------- Dialog Component ---------------- */

@Component({
  selector: "app-grocery-status-dialog",
  standalone: true,
  imports: [NgIf, MatDialogModule, MatButtonModule, HttpClientModule],
  template: `
    <div style="padding: 24px;">
      <h2 mat-dialog-title>🛒 Grocery List Generator</h2>
      <mat-dialog-content>
        <div style="white-space: pre-line;">{{ data.message }}</div>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button
          *ngIf="data.status !== 'processing'"
          mat-button
          mat-dialog-close
        >
          Close
        </button>
        <button *ngIf="showDownloadButton" mat-button (click)="downloadPDF()">
          Download PDF
        </button>
      </mat-dialog-actions>
    </div>
  `,
})
export class GroceryStatusDialogComponent {
  showDownloadButton = false;
  memberId: string;
  private groceryApiUrl: string = environment.groceryListApiUrl;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<GroceryStatusDialogComponent>,
    private http: HttpClient
  ) {
    this.memberId = data.memberId || "";
  }

  updateStatus(status: string, message: string, showDownload = false) {
    this.data = { status, message };
    this.showDownloadButton = showDownload;
  }

  downloadPDF() {
    this.http
      .post(
        `${this.groceryApiUrl}/download_pdf`,
        { member_id: this.memberId },
        { responseType: "blob" }
      )
      .subscribe((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `grocery_list_${this.memberId}_${Date.now()}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.dialogRef.close();
      });
  }
}

/* ---------------- Grocery Insights Dialog ---------------- */

@Component({
  selector: "app-grocery-insights-dialog",
  standalone: true,
  imports: [
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  template: `
    <div class="grocery-insights-dialog">
      <h2 mat-dialog-title class="grocery-insights-title">
        <span class="grocery-insights-icon">🛒</span>
        Grocery List Details
      </h2>
      <mat-dialog-content class="grocery-insights-content">
        <form [formGroup]="form" class="grocery-insights-form">
          <div class="grocery-insights-grid">
            <div class="grocery-insights-field">
              <label>From date</label>
              <input type="date" formControlName="fromDate" />
            </div>
            <div class="grocery-insights-field">
              <label>To date</label>
              <input type="date" formControlName="toDate" />
            </div>
          </div>
          <div class="grocery-insights-grid">
            <div class="grocery-insights-field">
              <label>Allergies</label>
              <input
                type="text"
                formControlName="allergies"
                placeholder="e.g. nuts, dairy, gluten"
              />
            </div>
            <div class="grocery-insights-field">
              <label>Preferred store</label>
              <input
                type="text"
                formControlName="preferredStore"
                placeholder="e.g. Whole Foods, Trader Joe's"
              />
            </div>
          </div>
          <div class="grocery-insights-notes-row">
            <div class="grocery-insights-field">
              <label>Food preference</label>
              <select formControlName="foodPreferences">
                <option value="">Select preference</option>
                <option value="vegetarian">Vegetarian (no eggs, no meat/fish)</option>
                <option value="vegetarian_with_eggs">
                  Vegetarian with eggs
                </option>
                <option value="vegan">Vegan (no animal products)</option>
                <option value="non_vegetarian">Non-vegetarian</option>
                <option value="jain_vegetarian">Jain vegetarian</option>
              </select>
            </div>
          </div>
        </form>
      </mat-dialog-content>
      <mat-dialog-actions align="end" class="grocery-insights-actions">
        <button mat-button mat-dialog-close>Cancel</button>
        <button mat-flat-button color="primary" (click)="start()">
          Start Grocery List
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [
    `
      .grocery-insights-dialog {
        padding: 0;
        max-width: 100%;
      }
      .grocery-insights-title {
        margin: 0;
        padding: 20px 24px 16px;
        font-size: 1.35rem;
        font-weight: 600;
        color: #000;
        display: flex;
        align-items: center;
        gap: 8px;
        border-bottom: 1px solid #e8e8e8;
        background: linear-gradient(to bottom, #fafafa 0%, #fff 100%);
      }
      .grocery-insights-icon {
        font-size: 1.5rem;
      }
      .grocery-insights-content {
        padding: 20px 24px 4px;
        min-width: 520px;
        max-width: 100%;
      }
      .grocery-insights-form {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }
      .grocery-insights-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 16px;
      }
      .grocery-insights-field {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .grocery-insights-field label {
        font-size: 0.9rem;
        font-weight: 500;
        color: #444;
      }
      .grocery-insights-field input,
      .grocery-insights-field textarea,
      .grocery-insights-field select {
        width: 100%;
        padding: 8px 10px;
        border-radius: 6px;
        border: 1px solid #d0d5dd;
        font-size: 0.9rem;
      }
      .grocery-insights-field input:focus,
      .grocery-insights-field textarea:focus,
      .grocery-insights-field select:focus {
        outline: none;
        border-color: #05445e;
        box-shadow: 0 0 0 1px rgba(5, 68, 94, 0.15);
      }
      .grocery-insights-actions {
        padding: 16px 24px 20px;
        border-top: 1px solid #e8e8e8;
      }
      .grocery-insights-notes-row {
        margin-top: 8px;
      }
      @media (max-width: 640px) {
        .grocery-insights-content {
          padding: 16px;
          min-width: 0;
        }
        .grocery-insights-grid {
          grid-template-columns: 1fr;
        }
      }
    `,
  ],
})
export class GroceryInsightsDialogComponent {
  form: FormGroup;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { form: FormGroup },
    private dialogRef: MatDialogRef<GroceryInsightsDialogComponent>
  ) {
    this.form = data.form;
  }

  start() {
    this.dialogRef.close("start");
  }
}

/* ---------------- Favorite Restaurants Dialog ---------------- */

@Component({
  selector: "app-favorite-restaurants-dialog",
  standalone: true,
  imports: [NgIf, NgFor, MatDialogModule, MatButtonModule, HttpClientModule],
  template: `
    <div class="fav-rest-dialog">
      <h2 mat-dialog-title class="fav-rest-title">
        <span class="fav-rest-title-icon">🍽️</span>
        Favorite Restaurants
        <span class="fav-rest-count" *ngIf="favorites?.length"
          >({{ favorites.length }})</span
        >
      </h2>
      <mat-dialog-content class="fav-rest-content">
        <p *ngIf="!favorites?.length" class="fav-rest-empty">
          No favorite restaurants yet.
        </p>
        <div *ngIf="favorites?.length" class="fav-rest-list">
          <div
            *ngFor="let fav of favorites; let i = index"
            class="fav-rest-item"
            [class.fav-rest-item-clickable]="fav.addedByMember === false"
            (click)="
              fav.addedByMember === false ? confirmUnfavorite(fav) : null
            "
          >
            <span class="fav-rest-number">{{ i + 1 }}</span>
            <div class="fav-rest-details">
              <span class="fav-rest-name">{{
                fav.restaurantName || "Unnamed"
              }}</span>
              <span *ngIf="fav.address" class="fav-rest-address"
                >📍 {{ fav.address }}</span
              >
              <span
                *ngIf="fav.addedByMember === false"
                class="fav-rest-recommended"
                title="Click to remove from favorites"
                >Recommended by Heald</span
              >
            </div>
          </div>
        </div>
      </mat-dialog-content>
      <mat-dialog-actions align="end" class="fav-rest-actions">
        <button mat-flat-button color="primary" mat-dialog-close>Close</button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [
    `
      .fav-rest-dialog {
        padding: 0;
        overflow: hidden;
      }
      .fav-rest-title {
        margin: 0;
        padding: 20px 24px 16px;
        font-size: 1.35rem;
        font-weight: 600;
        color: #000;
        display: flex;
        align-items: center;
        gap: 8px;
        border-bottom: 1px solid #e8e8e8;
        background: linear-gradient(to bottom, #fafafa 0%, #fff 100%);
      }
      .fav-rest-title-icon {
        font-size: 1.5rem;
      }
      .fav-rest-count {
        font-weight: 500;
        color: #666;
        font-size: 0.95rem;
      }
      .fav-rest-content {
        padding: 20px 24px;
        max-height: 65vh;
        overflow-y: auto;
      }
      .fav-rest-empty {
        color: #666;
        text-align: center;
        padding: 24px 16px;
        margin: 0;
      }
      .fav-rest-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .fav-rest-item {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 14px 16px;
        background: #f8f9fa;
        border-radius: 12px;
        border: 2px solid #05445e;
        transition: background 0.2s;
      }
      .fav-rest-item .fav-rest-details {
        flex: 1;
        min-width: 0;
      }
      .fav-rest-item:hover {
        background: #eef1f4;
      }
      .fav-rest-item-clickable {
        cursor: pointer;
      }
      .fav-rest-number {
        flex-shrink: 0;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: #05445e;
        color: #fff;
        font-size: 0.85rem;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
      }
      .fav-rest-name {
        display: block;
        font-weight: 600;
        color: #212529;
        font-size: 1rem;
        margin-bottom: 4px;
      }
      .fav-rest-heart {
        flex-shrink: 0;
        font-size: 1.25rem;
        align-self: center;
      }
      .fav-rest-address {
        display: block;
        color: #6c757d;
        font-size: 0.875rem;
        line-height: 1.4;
        word-break: break-word;
      }
      .fav-rest-recommended {
        margin-top: 10px;
        font-weight: 600;
        display: block;
        color: green;
        font-size: 0.875rem;
        line-height: 1.4;
        word-break: break-word;
      }
      .fav-rest-actions {
        padding: 16px 24px 20px;
        border-top: 1px solid #e8e8e8;
      }
    `,
  ],
})
export class FavoriteRestaurantsDialogComponent {
  favorites: any[] = [];
  memberId: string = "";
  removing: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: { favorites?: any[]; memberId?: string },
    private dialogRef: MatDialogRef<FavoriteRestaurantsDialogComponent>,
    private http: HttpClient,
    private sharedService: SharedServiceService,
    private toastService: ToastService
  ) {
    this.favorites = data?.favorites ?? [];
    this.memberId = data?.memberId ?? "";
  }

  confirmUnfavorite(fav: any): void {
    if (this.removing) return;
    const placeId = fav.placeId ?? fav.place_id;
    if (!placeId) {
      this.toastService.errorMessage(
        "Cannot remove: restaurant has no place ID."
      );
      return;
    }
    const confirmed = window.confirm(
      "Are you sure you want to remove this restaurant from your favorite list?"
    );
    if (!confirmed) return;

    this.removing = true;
    const url = `${environment.favoriteRestaurantsApiUrl}addRemoveFavouriteRestaurant`;
    const headers = this.sharedService.createAuthorizationHeaderForRestaurant();
    const body = {
      memberId: this.memberId,
      placeId,
      addedByMember: fav.addedByMember,
      restaurantName: fav.restaurantName ?? fav.name ?? "",
      address: fav.address ?? "",
    };

    this.http
      .post<{ success?: boolean; action?: string; message?: string }>(
        url,
        body,
        { headers }
      )
      .subscribe({
        next: (response) => {
          this.removing = false;
          this.toastService.successMessage(
            response?.message ?? "Restaurant removed from favorites."
          );
          this.favorites = this.favorites.filter(
            (f) => (f.placeId ?? f.place_id) !== placeId
          );
        },
        error: (err) => {
          this.removing = false;
          const msg =
            err.error?.error_message ??
            err.error?.error ??
            err.message ??
            "Failed to remove from favorites.";
          this.toastService.errorMessage(msg);
        },
      });
  }
}

/* ---------------- Add Restaurant Dialog ---------------- */

@Component({
  selector: "app-add-restaurant-dialog",
  standalone: true,
  imports: [
    NgIf,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
  ],
  template: `
    <div class="add-rest-dialog">
      <h2 mat-dialog-title class="add-rest-title">
        <span class="add-rest-title-icon">🔍</span>
        Search Restaurant
      </h2>
      <mat-dialog-content class="add-rest-content">
        <!-- Search Form (shown when restaurant not found yet) -->
        <form
          *ngIf="!restaurant"
          [formGroup]="restaurantForm"
          (ngSubmit)="onShow()"
        >
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Restaurant Name</mat-label>
            <input
              matInput
              formControlName="restaurantName"
              placeholder="Enter restaurant name"
              required
            />
            <mat-error
              *ngIf="restaurantForm.get('restaurantName')?.hasError('required')"
            >
              Restaurant name is required
            </mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Address</mat-label>
            <textarea
              matInput
              formControlName="address"
              placeholder="Enter restaurant address"
              rows="3"
              required
            ></textarea>
            <mat-error
              *ngIf="restaurantForm.get('address')?.hasError('required')"
            >
              Address is required
            </mat-error>
          </mat-form-field>
        </form>

        <!-- Loading State -->
        <div *ngIf="saving && !restaurant" class="search-loading">
          <p>Searching restaurant...</p>
        </div>

        <!-- Restaurant Display (shown when restaurant is found) -->
        <div *ngIf="restaurant" class="restaurant-display">
          <div class="restaurant-display-card">
            <div class="restaurant-display-icon">🍽️</div>
            <div class="restaurant-display-details">
              <h3 class="restaurant-display-name">
                {{ restaurant.restaurantName || restaurant.name || "Unnamed" }}
              </h3>
              <p *ngIf="restaurant.address" class="restaurant-display-address">
                <span class="address-icon">📍</span>
                {{ restaurant.address }}
              </p>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div *ngIf="errorMessage" class="error-message">
          <p>{{ errorMessage }}</p>
        </div>
      </mat-dialog-content>
      <mat-dialog-actions align="end" class="add-rest-actions">
        <button mat-button mat-dialog-close>Cancel</button>
        <button
          *ngIf="!restaurant"
          mat-flat-button
          color="primary"
          [disabled]="restaurantForm.invalid || saving"
          (click)="onShow()"
        >
          {{ saving ? "Searching..." : "Show" }}
        </button>
        <button
          *ngIf="restaurant"
          mat-flat-button
          color="primary"
          [disabled]="submitting"
          (click)="onSubmit()"
        >
          {{ submitting ? "Submitting..." : "Submit" }}
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [
    `
      .add-rest-dialog {
        padding: 0;
        overflow: hidden;
      }
      .add-rest-title {
        margin: 0;
        padding: 20px 24px 16px;
        font-size: 1.35rem;
        font-weight: 600;
        color: #000;
        display: flex;
        align-items: center;
        gap: 8px;
        border-bottom: 1px solid #e8e8e8;
        background: linear-gradient(to bottom, #fafafa 0%, #fff 100%);
      }
      .add-rest-title-icon {
        font-size: 1.5rem;
      }
      .add-rest-content {
        padding: 20px 24px;
        min-height: 180px;
      }
      .full-width {
        width: 100%;
        margin-bottom: 16px;
      }
      .search-loading {
        text-align: center;
        padding: 20px;
        color: #666;
      }
      .restaurant-display {
        margin-top: 8px;
      }
      .restaurant-display-card {
        display: flex;
        align-items: flex-start;
        gap: 16px;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 12px;
        border: 2px solid #05445e;
      }
      .restaurant-display-icon {
        font-size: 2rem;
        flex-shrink: 0;
      }
      .restaurant-display-details {
        flex: 1;
        min-width: 0;
      }
      .restaurant-display-name {
        font-size: 1.1rem;
        font-weight: 600;
        color: #212529;
        margin: 0 0 8px 0;
      }
      .restaurant-display-address {
        color: #6c757d;
        font-size: 0.9rem;
        line-height: 1.5;
        margin: 0;
        word-break: break-word;
      }
      .address-icon {
        margin-right: 4px;
      }
      .error-message {
        padding: 12px;
        background: #fee;
        border-radius: 8px;
        color: #c33;
        margin-top: 12px;
      }
      .add-rest-actions {
        padding: 16px 24px 20px;
        border-top: 1px solid #e8e8e8;
      }
    `,
  ],
})
export class AddRestaurantDialogComponent {
  restaurantForm: FormGroup;
  saving: boolean = false;
  submitting: boolean = false;
  restaurant: any = null;
  errorMessage: string = "";
  memberId: string = "";

  constructor(
    @Inject(MAT_DIALOG_DATA) data: { memberId: string },
    private dialogRef: MatDialogRef<AddRestaurantDialogComponent>,
    private fb: FormBuilder,
    private http: HttpClient,
    private sharedService: SharedServiceService,
    private toastService: ToastService
  ) {
    this.memberId = data?.memberId || "";
    this.restaurantForm = this.fb.group({
      restaurantName: ["", [Validators.required]],
      address: ["", [Validators.required]],
    });
  }

  onShow(): void {
    if (this.restaurantForm.invalid || this.saving) {
      return;
    }

    this.saving = true;
    this.errorMessage = "";
    this.restaurant = null;
    const formValue = this.restaurantForm.value;

    const url = `${environment.favoriteRestaurantsApiUrl}getRestaurantByNameOrAddress`;
    const headers = this.sharedService.createAuthorizationHeaderForRestaurant();

    this.http
      .post<{
        success?: boolean;
        restaurant?: any;
        restaurantName?: string;
        name?: string;
        address?: string;
      }>(
        url,
        {
          memberId: this.memberId,
          name: formValue.restaurantName,
          address: formValue.address,
        },
        { headers }
      )
      .subscribe({
        next: (response) => {
          this.saving = false;
          // Handle single restaurant response (similar structure to favorites but single object)
          if (response?.restaurant) {
            this.restaurant = response.restaurant;
          } else if (response?.restaurantName || response?.name) {
            // If response itself is the restaurant object
            this.restaurant = response;
          } else if (response?.success && response.restaurant) {
            this.restaurant = response.restaurant;
          } else {
            this.errorMessage =
              "Restaurant not found. Please check the name and address.";
          }
        },
        error: (err) => {
          this.saving = false;
          this.errorMessage =
            err.error?.error_message ||
            err.error?.error ||
            err.message ||
            "Failed to search restaurant.";
          this.toastService.errorMessage(this.errorMessage);
        },
      });
  }

  onSubmit(): void {
    if (!this.restaurant || this.submitting) {
      return;
    }

    const placeId =
      this.restaurant.placeId ??
      this.restaurant.place_id ??
      (this.restaurant as any).placeId;
    if (!placeId) {
      this.toastService.errorMessage(
        "Restaurant place ID is missing. Cannot add to favorites."
      );
      return;
    }

    this.submitting = true;
    const url = `${environment.favoriteRestaurantsApiUrl}addRemoveFavouriteRestaurant`;
    const headers = this.sharedService.createAuthorizationHeaderForRestaurant();

    const body = {
      memberId: this.memberId,
      placeId,
      addedByMember: false,
      restaurantName:
        this.restaurant.restaurantName ??
        this.restaurant.name ??
        this.restaurant.restaurantName ??
        "",
      address: this.restaurant.address ?? "",
      latitude: this.restaurant.latitude ?? this.restaurant.lat ?? undefined,
      longitude: this.restaurant.longitude ?? this.restaurant.lng ?? undefined,
      rating: this.restaurant.rating ?? undefined,
      priceLevel: this.restaurant.priceLevel ?? undefined,
    };

    this.http
      .post<{
        success?: boolean;
        isFavorited?: boolean;
        action?: string;
        message?: string;
      }>(url, body, { headers })
      .subscribe({
        next: (response) => {
          this.submitting = false;
          const msg = response?.message ?? "Restaurant added to favorites.";
          this.toastService.successMessage(msg);
          this.dialogRef.close({ success: true, restaurant: this.restaurant });
        },
        error: (err) => {
          this.submitting = false;
          const msg =
            err.error?.error_message ??
            err.error?.error ??
            err.message ??
            "Failed to add restaurant to favorites.";
          this.toastService.errorMessage(msg);
        },
      });
  }
}
